import { Tabs, TabsProps, Form } from "antd";
import dayjs from "dayjs";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useReactiveVar } from "@apollo/client";

import "./GoGoLiveDetail.scss";
import GoGoLiveDetailMain from "./GoGoLiveDetailMain";
import GoGoLiveDetailTabLocale from "./GoGoLiveDetailTabLocale";
import Spinner from "../../../../components/layout/spinner/Spinner";
import MainContentHeader from "../../../../components/layout/mainContent/MainContentHeader";
import { useGoGoLiveParentGuideInfoLazyQuery } from "../../../../graphql/operations/clubApp/ves/get-gogolive-parent-guide-info";
import { useGoGoLiveParentGuideLocaleListLazyQuery } from "../../../../graphql/operations/clubApp/ves/get-gogolive-parent-guide-locale-list";
import { GoGoLiveParentGuideDetailInfo } from "../../../../graphql/operations/@types";
import { __currentUser__ } from "../../../../graphql/policies";
import { ServiceTypes } from "../../../../types/types";

interface GoGoLiveDetailProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const GoGoLiveDetail = ({
  setSelectedService,
  setSelectSubItem,
}: GoGoLiveDetailProps) => {
  const currentUser = useReactiveVar(__currentUser__);
  const userName = currentUser?.userName;
  const { id, type }: any = useParams();
  const [MainForm] = Form.useForm();
  const [mainInfo, setMainInfo] = useState<GoGoLiveParentGuideDetailInfo>();
  const [HKForm] = Form.useForm();
  const [TWForm] = Form.useForm();
  const [KRForm] = Form.useForm();
  const [canEditEN, setCanEditEN] = useState<boolean>(false);
  const [canEditHK, setCanEditHK] = useState<boolean>(false);
  const [canEditTW, setCanEditTW] = useState<boolean>(false);
  const [canEditKR, setCanEditKR] = useState<boolean>(false);
  const [isView, setIsView] = useState<boolean>(false);

  //editMode >> 0: add 1: update
  const [editModeHK, setEditModeHK] = useState(0);
  const [editModeTW, setEditModeTW] = useState(0);
  const [editModeKR, setEditModeKR] = useState(0);

  const mainType = type?.toUpperCase() ?? mainInfo?.serviceType;

  const title = mainType === "CAPK" ? "CAP Kids detail"
    : mainType === "FAFU" ? "Family Fun detail"
      : mainType === "PLYT" ? "Playtime detail"
        : "detail"

  const [getGoGoLiveParentGuideInfo, { loading: fetchInfoLoading }] =
    useGoGoLiveParentGuideInfoLazyQuery({
      variables: { goGoLiveParentGuideId: parseInt(id) },
      fetchPolicy: "no-cache",
    });

  const [getGoGoLiveParentGuideLocaleList, { loading: fetchLocaleListLoading }] =
    useGoGoLiveParentGuideLocaleListLazyQuery({
      variables: { goGoLiveParentGuideId: parseInt(id) },
      fetchPolicy: "no-cache",
    });

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "English",
      children: (
        <GoGoLiveDetailMain
          mainType={mainType}
          form={MainForm}
          canEdit={canEditEN}
          isView={isView}
          userName={userName}
        />
      ),
    },
    {
      key: "2",
      label: "Hong Kong",
      children: (
        <GoGoLiveDetailTabLocale
          mainInfo={mainInfo}
          localeForm={HKForm}
          market="HK"
          canEdit={canEditHK}
          isView={isView}
          editMode={editModeHK}
          userName={userName}
        />
      ),
    },
    {
      key: "3",
      label: "Taiwan",
      children: (
        <GoGoLiveDetailTabLocale
          mainInfo={mainInfo}
          localeForm={TWForm}
          market="TW"
          canEdit={canEditTW}
          isView={isView}
          editMode={editModeTW}
          userName={userName}
        />
      ),
    },
    {
      key: "4",
      label: "Korea",
      children: (
        <GoGoLiveDetailTabLocale
          mainInfo={mainInfo}
          localeForm={KRForm}
          market="KR"
          canEdit={canEditKR}
          isView={isView}
          editMode={editModeKR}
          userName={userName}
        />
      ),
    },
  ];

  useEffect(() => {
    setSelectedService(ServiceTypes.CLUB_APP);
    setSelectSubItem("Go, Go Live!");
  }, []);

  useEffect(() => {
    if (currentUser?.afUserMarkets) {
      const market = currentUser.afUserMarkets.map((item) => item?.market);
      if (currentUser.isAdministrator &&
        market.includes("HK") && market.includes("TW") && market.includes("KR")) {
        setCanEditEN(true);
        setCanEditHK(true);
        setCanEditTW(true);
        setCanEditKR(true);
      } else if (!currentUser.isAdministrator &&
        market.includes("HK") && market.includes("TW") && market.includes("KR")) {
        setCanEditEN(true);
      } else {
        if (market.includes("HK")) {
          setCanEditHK(true);
        }
        if (market.includes("TW")) {
          setCanEditTW(true);
        }
        if (market.includes("KR")) {
          setCanEditKR(true);
        }
      }
    }

    if (id) {
      getGoGoLiveParentGuideInfo()
        .then((r) => {
          if (r.data?.goGoLiveParentGuideInfo) {
            const info = r.data?.goGoLiveParentGuideInfo?.[0];
            MainForm.setFieldsValue({
              ...info,
              yearAndMonth: info?.yearAndMonth ? dayjs(info.yearAndMonth) : null,
              sessions: info?.sessions.map(item => item.sessionId),
              previewVideoCoverImageFileName: info?.previewVideoCoverImageFileName ? [{
                uid: "0",
                name: decodeURIComponent(info?.previewVideoCoverImageFileName.split('/').pop() || ""),
                status: "done",
                url: info?.previewVideoCoverImageFileName,
              },] : [],
              songList: info?.songList?.map(song => ({
                discCode: song.discCode,
                eTabCode: song.eTabCode,
                songs: song.songs.map(s => s.songId),
              })),
              wordList: info?.wordList.map(item => item.talkAlongCardId),
              toys: info?.toys.map(item => item.toyId),
              printableSheet: info?.printableSheet ? [{
                uid: "0",
                name: info?.printableSheet.split('/').pop(),
                status: "done",
                url: info?.printableSheet,
              },] : []
            })
            setMainInfo(info);

            if (info.yearAndMonth && dayjs(info.yearAndMonth).isBefore(dayjs(), "month")) {
              setCanEditEN(false);
              setCanEditHK(false);
              setCanEditTW(false);
              setCanEditKR(false);
              setIsView(true);
            }

            if (info.serviceType === "GGLA") {
              getGoGoLiveParentGuideLocaleList()
                .then((r) => {
                  if (r.data?.goGoLiveParentGuideLocaleList) {
                    const LocaleList = r.data?.goGoLiveParentGuideLocaleList;

                    const hkInfo = LocaleList.find(item => item.market === "HK");
                    const twInfo = LocaleList.find(item => item.market === "TW");
                    const krInfo = LocaleList.find(item => item.market === "KR");
                    if (hkInfo) {
                      HKForm.setFieldsValue({
                        ...hkInfo,
                        learningGoals: (hkInfo.learningGoals === "" ? [""] : JSON.parse(hkInfo.learningGoals))
                      });
                      setEditModeHK(1);
                    }

                    if (twInfo) {
                      TWForm.setFieldsValue({
                        ...twInfo,
                        learningGoals: (twInfo.learningGoals === "" ? [""] : JSON.parse(twInfo.learningGoals))
                      });
                      setEditModeTW(1);
                    }

                    if (krInfo) {
                      KRForm.setFieldsValue({
                        ...krInfo,
                        learningGoals: (krInfo.learningGoals === "" ? [""] : JSON.parse(krInfo.learningGoals))
                      });
                      setEditModeKR(1);
                    }
                  }
                })
                .catch((e) => console.log(e));
            }
          }
        })
        .catch((e) => console.log(e));
    }
    else {
      setCanEditHK(false);
      setCanEditTW(false);
      setCanEditKR(false);
    }
  }, [currentUser, id]);

  if (fetchInfoLoading || fetchLocaleListLoading) {
    return <Spinner />;
  }

  return (
    <div id="GoGoLiveDetailWrapper">
      <div style={{ position: "relative", paddingBottom: 48 }}>
        <MainContentHeader
          title={`Go, Go Live! ${title}`}
        />
        {(mainInfo && mainInfo.serviceType === "GGLA") || type === "ggl" ? (
          <Tabs
            defaultActiveKey="1"
            items={items}
            size="small"
            tabBarStyle={{
              paddingLeft: 24,
              height: 50,
            }}
          />
        ) : (
          <div className="content-container">
            <GoGoLiveDetailMain
              mainType={mainType}
              form={MainForm}
              canEdit={true}
              isView={isView}
              userName={userName}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GoGoLiveDetail;
